import type { ReactNode } from 'react';
import { Flame, Lightbulb, Shapes, ThumbsUp, TrendingDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Skeleton } from '@/components/ui/skeleton';
import type { CompetitionInsights } from '@/lib/competitions/competitionInsights';

interface CompetitionInsightsPanelProps {
  /** null while loading (or after a failed load). */
  insights: CompetitionInsights | null;
  failed: boolean;
  /** The competition's habit count, for «X of Y habits». */
  habitCount: number;
}

/**
 * «مؤشرات المسابقة» (namat#35): most/least kept habit, longest streak and most
 * variety. Shared by the manager's competition details and the beneficiary's
 * details dialog (namat#36) so both read the same four cards. Names only.
 */
export function CompetitionInsightsPanel({ insights, failed, habitCount }: CompetitionInsightsPanelProps) {
  const { t, dir } = useLanguage();
  const align = dir === 'rtl' ? 'text-right' : 'text-left';
  const insightLabel = (key: string, values?: Record<string, string | number>) =>
    t(`dashboard.manager.competitions.insights.${key}`, values);

  const card = (
    testId: string,
    icon: ReactNode,
    title: string,
    value: string | undefined,
    detail: string | undefined,
    ties: number,
  ) => (
    <div className="rounded-lg border p-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        {title}
      </div>
      {insights === null ? (
        failed ? <div className="mt-1 font-semibold">—</div> : <Skeleton className="h-5 w-24 mt-2" />
      ) : value === undefined ? (
        <div className="mt-1 text-sm text-muted-foreground">{insightLabel('none')}</div>
      ) : (
        <>
          <div className="mt-1 font-semibold">{value}</div>
          {detail && <div className="text-xs text-muted-foreground">{detail}</div>}
          {ties > 0 && <div className="text-xs text-muted-foreground">{insightLabel('ties', { count: ties })}</div>}
        </>
      )}
    </div>
  );

  const percent = (rate: number) => Math.round(rate * 100);
  const mostKept = insights?.mostKeptHabit ?? undefined;
  const leastKept = insights?.leastKeptHabit ?? undefined;
  const streak = insights?.longestStreak ?? undefined;
  const variety = insights?.mostVariety ?? undefined;

  return (
    <section aria-labelledby="competition-insights-title">
      <h4 id="competition-insights-title" className={`flex items-center gap-2 text-sm font-semibold mb-2 ${align}`}>
        <Lightbulb className="h-4 w-4" aria-hidden />
        {insightLabel('title')}
      </h4>
      <div className="grid gap-2 sm:grid-cols-2">
        {card(
          'insight-most-kept',
          <ThumbsUp className="h-4 w-4 text-emerald-600" aria-hidden />,
          insightLabel('mostKept'),
          mostKept?.name,
          mostKept && insightLabel('habitDetail', { count: mostKept.completed, percent: percent(mostKept.rate) }),
          mostKept?.ties ?? 0,
        )}
        {card(
          'insight-least-kept',
          <TrendingDown className="h-4 w-4 text-amber-600" aria-hidden />,
          insightLabel('leastKept'),
          leastKept?.name,
          leastKept && insightLabel('habitDetail', { count: leastKept.completed, percent: percent(leastKept.rate) }),
          leastKept?.ties ?? 0,
        )}
        {card(
          'insight-longest-streak',
          <Flame className="h-4 w-4 text-orange-500" aria-hidden />,
          insightLabel('longestStreak'),
          streak?.name,
          streak && insightLabel('streakDetail', { count: streak.value }),
          streak?.ties ?? 0,
        )}
        {card(
          'insight-most-variety',
          <Shapes className="h-4 w-4 text-sky-600" aria-hidden />,
          insightLabel('mostVariety'),
          variety?.name,
          variety && insightLabel('varietyDetail', { count: variety.value, total: habitCount }),
          variety?.ties ?? 0,
        )}
      </div>
      {insights && (
        <p className={`mt-2 text-xs text-muted-foreground ${align}`}>
          {insightLabel('rateNote', { participants: insights.participants, days: insights.elapsedDays })}
        </p>
      )}
    </section>
  );
}
